import { track } from "@/features/analytics";
import { useGetSearchTermMutation } from "@api/ai";
import { useSearchImagesMutation } from "@api/media";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  IMAGES_PANEL_STOCK_CATEGORIES,
  STOCK_IMAGES_QUERY_KEYS,
} from "../constants";
import { useImagesPanelStockStoreShallow } from "../store";
import { prepareStockImagesAiPrompt } from "../utils";

const PER_PAGE = 30;

interface IProps {
  searchQuery: string;
}

interface IFetchProps {
  replace?: boolean;
  signal?: AbortSignal;
  nextPage?: boolean;
  query?: string;
}

export const useFetchStockImages = ({ searchQuery }: IProps) => {
  const { category, columnsData, setColumnsData } =
    useImagesPanelStockStoreShallow((s) => ({
      columnsData: s.columnsData,
      setColumnsData: s.setColumnsData,
      category: s.category,
    }));

  const isInit = useRef(true);
  const [isReplacingImages, setIsReplacingImages] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isError, setIsError] = useState(false);
  const [reloadKey, setReloadKey] = useState({});
  const lastSystemQuery = useRef("");
  const latestPage = useRef(0);

  const [fetchImages] = useSearchImagesMutation({
    fixedCacheKey: STOCK_IMAGES_QUERY_KEYS,
  });
  const [getSearchTerm] = useGetSearchTermMutation();

  const fetchPageOfImages = async ({
    query = searchQuery || lastSystemQuery.current,
    signal,
    replace,
    nextPage,
  }: IFetchProps) => {
    setIsError(false);
    setIsFetchingMore(true);
    try {
      if (replace) {
        setIsReplacingImages(true);
        latestPage.current = 0;
      } else if (nextPage) {
        setIsFetchingMore(true);
        latestPage.current++;
      }

      const data = await fetchImages({
        page: latestPage.current,
        per_page: PER_PAGE,
        query: query,
        signal: signal,
      }).unwrap();

      setColumnsData({
        images: data.results,
        extend: nextPage,
      });
    } catch (e) {
      console.log(e);
      track("error fetching unsplash images", {
        error: e,
        query: query,
      });
      setIsError(true);
    } finally {
      setIsReplacingImages(false);
      setIsFetchingMore(false);
    }
  };

  const replaceImagesData = async (controller: AbortController) => {
    setIsError(false);
    setIsReplacingImages(true);

    let query = searchQuery;
    if (query.length === 0) {
      const found = IMAGES_PANEL_STOCK_CATEGORIES.find(
        (c) => c.key === category,
      );
      if (found) {
        if (found.key === "ai_suggestions") {
          const prompt = prepareStockImagesAiPrompt();
          query = IMAGES_PANEL_STOCK_CATEGORIES[1].query;
          if (prompt) {
            try {
              const answer = (await getSearchTerm({
                prompt,
                signal: controller.signal,
                type: "json_object",
              }).unwrap()) as { search?: string };

              if (
                typeof answer === "object" &&
                Object.hasOwn(answer, "search") &&
                typeof answer.search === "string"
              ) {
                query = answer.search;
              }
            } catch (e) {
              console.log(e);
              track("error generating ai search terms", {
                error: e,
                prompt,
              });
            }
          }
        } else {
          query = found.query;
        }
      }
    }
    if (controller.signal.aborted) {
      setIsReplacingImages(false);
      return;
    }

    lastSystemQuery.current = query;
    await fetchPageOfImages({
      query,
      replace: true,
      signal: controller.signal,
    });
    setIsReplacingImages(false);
  };

  useEffect(() => {
    const controller = new AbortController();
    debounce(async () => {
      if (isInit.current) {
        isInit.current = false;
        if (columnsData[0].length === 0) {
          replaceImagesData(controller);
        } else {
          setIsReplacingImages(false);
        }
        return;
      }

      replaceImagesData(controller);
    }, 100)();

    return () => {
      controller.abort();
    };
  }, [searchQuery, category, reloadKey]);

  const canLoadMoreImages = !isFetchingMore && !isReplacingImages;

  const fetchMoreImages = useCallback(
    debounce(() => {
      if (
        !canLoadMoreImages ||
        columnsData.reduce((sum, col) => sum + col.length, 0) < PER_PAGE / 2
      ) {
        return;
      }
      fetchPageOfImages({ nextPage: true });
    }, 100),
    [canLoadMoreImages, columnsData, fetchPageOfImages],
  );

  return {
    isError,
    reloadData: () => setReloadKey({}),
    fetchMoreImages,
    shouldShowSkeleton: isReplacingImages,
    canLoadMoreImages,
  };
};
